import { useEffect, useRef, useState } from 'react';

import { extractSmartLinksFromAdf } from '@confluence/connectors/entry-points/extractSmartLinksFromAdf.ts';
import { usePageAdf } from '@confluence/connectors/entry-points/usePageAdf.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';

import { chooseMostReferencedProduct } from './page-links/chooseMostReferencedProduct.ts';
import type { QualifyingProduct, ThirdPartyPromptState } from './thirdPartyPromptTypes.ts';

export type ThirdPartyPromptSlotResult = {
	/** Undefined once resolved means the slot goes back to the first-party cards. */
	product: QualifyingProduct | undefined;
	isResolved: boolean;
};

const UNRESOLVED: ThirdPartyPromptSlotResult = { product: undefined, isResolved: false };
const NO_SLOT: ThirdPartyPromptSlotResult = { product: undefined, isResolved: true };
const ADF_WAIT_TIMEOUT_MS = 10000;

type ContentSlot = ThirdPartyPromptSlotResult & { contentId: string };

/**
 * Which product fills the reserved slot. A single candidate resolves during server render. Several
 * candidates stay unresolved until the document names the most referenced product.
 */
export const useThirdPartyPromptProduct = (
	state: ThirdPartyPromptState,
	contentId: string,
): ThirdPartyPromptSlotResult => {
	const { adf } = usePageAdf(contentId);
	const [slot, setSlot] = useState<ContentSlot | undefined>();

	// Detection runs on every render, so its result is read through a ref. Keeping it out of the deps
	// is what stops an ancestor re-render, on scroll for example, re-walking the document and flashing
	// the skeleton back. Follows useSmartLinks.ts:81.
	const stateRef = useRef(state);
	stateRef.current = state;

	const { kind } = state;

	useEffect(() => {
		if (kind !== 'many') {
			return;
		}

		const resolve = (product?: QualifyingProduct) => {
			setSlot({ contentId, product, isResolved: true });
		};

		const current = stateRef.current;
		if (current.kind !== 'many') {
			return;
		}

		if (!adf) {
			const timeoutId = setTimeout(() => resolve(), ADF_WAIT_TIMEOUT_MS);
			return () => clearTimeout(timeoutId);
		}

		// `true` includes link marks. Without it the walk finds card nodes only and misses every plain
		// hyperlink, which is the whole reason this feature needs the walk.
		try {
			const links = extractSmartLinksFromAdf(adf, true);
			resolve(chooseMostReferencedProduct(current.candidates, links));
		} catch (error) {
			getMonitoringClient().submitError(error instanceof Error ? error : new Error(String(error)), {
				attribution: Attribution.INTEGRATIONS,
			});
			resolve();
		}
	}, [adf, contentId, kind]);

	if (kind === 'none') {
		return NO_SLOT;
	}
	if (kind === 'single') {
		return { product: state.product, isResolved: true };
	}

	return kind === 'pending' || slot?.contentId !== contentId
		? UNRESOLVED
		: { product: slot.product, isResolved: slot.isResolved };
};
